import * as i18nData from '@mc-alberta/i18n-data'
import { i18nAdapter, joinAddressNames, splitAddressNames } from '@mc-alberta/supernova-scripts'
import { isTestOverrideEnabled } from '@mc-alberta/switchboard'
import Vue from 'vue'
import appInstances from '@/api/appInstances'
import bind from '@/api/bind'
import consumers from '@/api/consumers'
import consumersProfile from '@/api/consumersProfile'
import unprovisionedProfile from '@/api/unprovisionedProfile'
import {
  ACTION,
  ANALYTICS,
  DEFAULT_APP_INSTANCES_LIMIT,
  TEST_OVERRIDES,
  VALIDATION_CHANNEL
} from '@/constants'
import * as types from '@/store/mutation-types'
import formatDateData from '@/utils/formatDateData'
import { getLatestVersion, versionCompare } from '@/utils/versionCompare'

const { determineBrowserLocale, determineLocale, loadOneTrust, localizeApp } = i18nAdapter

/**
 * @fileoverview this file contains our user module for our Vuex store.
 * Anything that logically relates to user data belongs here.
 */

// default these until we load consumer data
export const consumerDefault = {
  id: '',
  email: '',
  phone: {
    countryCode: '',
    mobileNumber: ''
  },
  settings: {
    marketingOptIn: false
  },
  countryCode: 'US',
  languageCode: 'en',
  status: '',
  firstName: '',
  lastName: '',
  nationalId: ''
}

const state = {
  consumer: consumerDefault,
  appInstances: [],
  callAppInstances: true,
  isLocaleValidated: false,
  initialLocale: null,
  previousLocale: null,
  experiments: []
}

const getters = {
  isLoggedIn({ consumer }) {
    return !!consumer.id
  },
  sortedAppInstances(state) {
    const sorter = (a, b) => b.lastUsedTime.getTime() - a.lastUsedTime.getTime()
    return [...state.appInstances].sort(sorter)
  },
  formattedUserEmail(state) {
    return state.consumer.email.replace(/\*/g, '•')
  },
  formattedUserMobileNumber({ consumer }) {
    return (app, phone) => {
      // we dont have access to the app instance from vuex, so it must be provided to vuex.
      // the facade formatting function is provided by alcapone ~Ronald
      const formatter = app.$options?.filters?.facade
      const { countryCode, mobileNumber } = phone || consumer.phone

      if (!formatter) {
        console.warn('Vue Input Facade formatter method not available')
        return `+${countryCode} ${mobileNumber.replace(/\*/g, '•')}`
      }

      const mask = i18nData.getPhoneMaskingRule(countryCode, consumer.countryCode)
      const tokens = { '#': { pattern: /[\d*]/ } }
      const formattedNumber = formatter(mobileNumber, { mask, tokens })

      return `+${countryCode} ${formattedNumber.replace(/\*/g, '•')}`
    }
  },
  /**
   * Returns general purpose locale formatted as en-US
   * @returns {String}
   */
  locale({ consumer }) {
    return i18nData.composeLocaleFromParts({
      languageCode: consumer.languageCode,
      countryCode: consumer.countryCode
    })
  },
  /**
   * Whether or not countryCode portion of locale changed when compared to previousLocale
   * @returns {Boolean}
   */
  countryChanged(state, getters) {
    const { countryCode } = i18nData.getLocaleParts(getters.locale)
    const { countryCode: previousCountryCode } = i18nData.getLocaleParts(state.previousLocale)
    return !!previousCountryCode && previousCountryCode !== countryCode
  },
  /**
   * Whether or not languageCode portion of locale changed when compared to previousLocale
   * @returns {Boolean}
   */
  languageChanged(state, getters) {
    const { languageCode } = i18nData.getLocaleParts(getters.locale)
    const { languageCode: previousLanguageCode } = i18nData.getLocaleParts(state.previousLocale)
    return !!previousLanguageCode && previousLanguageCode !== languageCode
  }
}

const actions = {
  /**
   * Binds additional data for user to a card with a given id.
   *
   * @param {Vuex} store
   * @param {Object} payload
   * @param {Object} [payload.billingAddress] - billing address to associate
   * @param {Object} [payload.shippingAddress] - shipping address to associate
   */
  async bindAdditionalUserData({ commit, dispatch, rootState }, payload) {
    try {
      const { srcDigitalCardId } = rootState.cards.newCard
      const normalizedBillingAddress = joinAddressNames(payload?.billingAddress)
      const normalizedShippingAddress = joinAddressNames(payload?.shippingAddress)

      const requestPayload = {
        ...(normalizedBillingAddress && { billingAddress: normalizedBillingAddress }),
        ...(normalizedShippingAddress && { shippingAddress: normalizedShippingAddress }),
        srcDigitalCardId
      }

      const { data } = await bind(requestPayload, rootState.cards.cardToken)

      // bind does not currently return the stored billing address
      // so we'll have to use what the user gave us for now ~ Ronald
      commit(types.SET_NEW_CARD_DATA, {
        card: {
          ...rootState.cards.newCard,
          ...(normalizedBillingAddress && { billingAddress: normalizedBillingAddress }),
          srcDigitalCardId: data?.srcDigitalCardId
        }
      })

      dispatch('addNewCard')
    } catch (error) {
      dispatch('setServiceLayerError', error)
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
    }
  },

  /**
   * Stores the new card and deletes the temporary card data.
   *
   * @param {Vuex} store
   */
  async addNewCard({ commit, rootGetters }) {
    if (rootGetters.isDuplicateCard) return

    commit(types.ADD_NEW_CARD)
    commit(types.DELETE_NEW_CARD_DATA)
  },

  /**
   * Complete a user registration by associating the new user with a new card.
   * User's billing and shipping address may also be bound to the card by providing
   * them along with the user information.
   *
   * @param {Vuex} store
   * @param {Object} payload
   * @param {Object} payload.consumer - consumer object
   * @param {Object} [payload.billingAddress] - billing address to associate
   * @param {Object} [payload.shippingAddress] - shipping address to associate
   */
  async registerUser({ commit, dispatch, rootState }, payload) {
    try {
      const { srcDigitalCardId } = rootState.cards.newCard
      const normalizedBillingAddress = joinAddressNames(payload?.billingAddress)
      const normalizedShippingAddress = joinAddressNames(payload?.shippingAddress)

      const requestPayload = {
        ...payload,
        ...(normalizedBillingAddress && { billingAddress: normalizedBillingAddress }),
        ...(normalizedShippingAddress && { shippingAddress: normalizedShippingAddress }),
        srcDigitalCardId
      }

      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, false)
      const { data } = await bind(requestPayload, rootState.cards.cardToken)
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)

      dispatch('setAuthorization', data.authorization)
    } catch (error) {
      dispatch('setServiceLayerError', error)
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
    }
  },

  /**
   * Retrieves the consumer profile. Stores the consumer data and shipping addresses.
   *
   * @param {Vuex} store
   */
  async loadCustomerProfile({ commit, dispatch }) {
    try {
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, false)
      const { data: profile } = await consumersProfile()
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)

      if (profile?.consumer) {
        const { consumer, cards, shippingAddresses, latestPolicies, appInstances } = profile

        const provisionedCards = cards.filter(({ status }) => {
          return status !== 'PENDING'
        })
        const unprovisionedCards = cards.filter(({ status }) => {
          return status === 'PENDING'
        })

        dispatch('setConsumer', consumer)

        if (!consumer.id && unprovisionedCards.length > 0) {
          commit(types.SET_ANALYTICS_FEATURE, ANALYTICS.FEATURES.BULK_PROVISIONING)
          dispatch('setAction', ACTION.BULK_PROVISION)
        }

        commit(types.SET_CARDS, provisionedCards)
        commit(types.SET_UNPROVISIONED_CARDS, unprovisionedCards)
        commit(types.SET_SHIPPING_ADDRESSES, shippingAddresses)
        commit(types.SET_APP_INSTANCES, appInstances)

        await dispatch('setConsumerSettingsData', { consumer, appInstances, latestPolicies })

        if (appInstances?.length < DEFAULT_APP_INSTANCES_LIMIT) {
          // appInstances are paginated to the first 10 items, change this value if the limit changes
          // or if profile gives us an appInstances.total value, check equality with that instead
          commit(types.SET_CALL_APP_INSTANCES, false)
        }
      }
    } catch (error) {
      dispatch('setServiceLayerError', error)
      commit(types.SET_INTERCEPT_UNAUTHORIZED_ERROR, true)
    }
  },

  /**
   * Get the unprovisioned profile corresponding to a specific card id
   *
   * @param {Vuex} store
   * @param {String} cardId - card id we to fetch the unprovisioned profile for
   */
  async fetchUnprovisionedProfile({ commit, dispatch }, cardId) {
    try {
      const { data } = await unprovisionedProfile.updateProfile({ unprovisionedProfileId: cardId })
      const { authorization, billingAddress, card, provisionStatus } = data
      const normalizedBillingAddress = splitAddressNames(billingAddress)

      commit(types.SET_NEW_CARD_DATA, { card, token: authorization?.[0] })
      commit(types.SET_PROVISIONING_DATA, {
        billingAddress: normalizedBillingAddress,
        status: provisionStatus
      })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  async setConsumerSettingsData({ commit, dispatch }, { consumer, appInstances, latestPolicies }) {
    if (consumer.settings) {
      if (!consumer.settings.tncVersion) {
        commit(types.SET_MUST_ACCEPT_POLICY, true)
      }

      if (consumer.settings && appInstances) {
        await dispatch('legalVersionUpdates', { consumer, latestPolicies })
      }
    }
  },

  async legalVersionUpdates({ commit, dispatch }, { consumer, latestPolicies }) {
    const tncDifference = versionCompare(
      consumer.settings.tncVersion,
      getLatestVersion(latestPolicies, 'tnc')
    )
    const privacyDifference = versionCompare(
      consumer.settings.privacyVersion,
      getLatestVersion(latestPolicies, 'privacy')
    )
    if (tncDifference) {
      commit(types.SET_MUST_REACCEPT, tncDifference)
    } else if (privacyDifference) {
      await dispatch('updateLegalAcceptedVersion')
    }
  },

  /**
   * Retrieves list of app instances from account management service as opposed to consumer profile
   * Result is merged into the store's existing appInstances list
   *
   * @param {Vuex} store
   */
  async loadAppInstances({ commit, dispatch, getters }, options) {
    try {
      const offset = Math.floor(getters.sortedAppInstances.length / DEFAULT_APP_INSTANCES_LIMIT)
      const { data } = await appInstances.getAppInstances({ offset, ...options })
      commit(types.ADD_APP_INSTANCES, data.items)

      if (data.total < DEFAULT_APP_INSTANCES_LIMIT) {
        commit(types.SET_CALL_APP_INSTANCES, false)
      }
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Removes the app instance by id
   * @param {Vuex} store
   * @param {String} appInstanceId
   */
  async deleteAppInstance({ commit, dispatch }, appInstanceId) {
    try {
      await appInstances.deleteAppInstance(appInstanceId)
      commit(types.DELETE_APP_INSTANCE, appInstanceId)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Update marketing opt in setting
   *
   * @param {Vuex} store
   * @param {Object} payload
   * @param {String} payload.consumerId
   * @param {Boolean} payload.marketingOptIn
   */
  async updateMarketingOptIn({ commit, dispatch }, { consumerId, marketingOptIn }) {
    try {
      await consumers.update(consumerId, { settings: { marketingOptIn } })
      commit(types.SET_MARKETING_OPT_IN, marketingOptIn)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Updates the languageCode selected by the user
   *
   * @param {Vuex} store
   * * @param {Vuex} dispatch
   * @param {String} languageCode
   */
  async updateLanguage({ state, dispatch }, languageCode) {
    try {
      const consumerId = state.consumer.id
      await consumers.update(consumerId, { languageCode })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Initialize update of phone number in setting
   *
   * @param {Vuex} store
   * @param {Object} phone
   * @param {String} phone.countryCode
   * @param {String} phone.mobileNumber
   * @param {String} cardAuthToken
   */

  async verifyChannel({ state, dispatch, commit }, { phone, cardAuthToken }) {
    const { id } = state.consumer
    const channel = { phone, type: VALIDATION_CHANNEL.SMS }

    try {
      const { data } = await consumers.verifyChannel({
        id,
        payload: { channel },
        cardAuthToken
      })
      const { verificationId } = data

      commit(types.SET_VERIFICATION_CHANNEL_USED, VALIDATION_CHANNEL.SMS)
      dispatch('setVerificationData', {
        idVerificationId: verificationId,
        contactInfo: phone
      })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Update phone number in setting
   *
   * @param {Vuex} store
   * @param {String} otp
   */
  async updateChannel({ state, commit, dispatch, rootState }, { otp, phone }) {
    try {
      const { verificationId, contactInfo } = rootState.identity.verificationData
      const { countryCode, mobileNumber } = contactInfo
      const { id } = state.consumer
      const channel = { type: VALIDATION_CHANNEL.SMS, phone }
      const cardAuthToken = rootState.cards.cardAuthToken

      await consumers.updateChannel({
        id,
        payload: { verificationId, otp, channel },
        cardAuthToken
      })
      commit(types.SET_MOBILE_NUMBER, { countryCode, mobileNumber })
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Update accepted legal policy versions
   *
   * @param {Vuex} store
   */
  async updateLegalAcceptedVersion(store) {
    const { commit, state, dispatch } = store
    const { id: srcConsumerId } = state.consumer
    const policies = {
      privacyVersion: 'latest',
      tncVersion: 'latest',
      cookieVersion: '1.0'
    }
    const payload = {
      settings: policies
    }

    try {
      await consumers.update(srcConsumerId, payload)
      commit(types.SET_COMPLIANCE_ACCEPTED_VERSION, policies)
      commit(types.SET_MUST_REACCEPT, false)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * resets this module back to default state
   * except for locale-related data (countryCode and languageCode)
   *
   * @param {Vuex} store
   */
  reset({ state, commit }) {
    const { countryCode, languageCode } = state.consumer

    commit(types.SET_CONSUMER, { ...consumerDefault, countryCode, languageCode })
    commit(types.SET_APP_INSTANCES)
    commit(types.SET_CALL_APP_INSTANCES, true)
  },

  /**
   * delete user profile and log user out
   *
   * @param {Vuex} store
   */
  async deleteProfile({ state, dispatch }) {
    const { id } = state.consumer

    try {
      await consumers.delete(id)
      dispatch('logout')
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  setConsumer({ state, commit, dispatch }, consumer) {
    // Determine the locale from the loaded consumer profile that is not yet on the model
    const locale = determineLocale(
      i18nData.composeLocaleFromParts({
        languageCode: consumer.languageCode,
        countryCode: consumer.countryCode
      })
    )

    // Enforce upper case first names for display because the database does not
    if (consumer.firstName) {
      consumer.firstName =
        consumer.firstName.charAt(0).toLocaleUpperCase(locale || undefined) +
        consumer.firstName.slice(1)
    }

    const { countryCode, languageCode } = state.consumer
    // Use default countryCode and languageCode if not received from the loaded consumer profile
    consumer.countryCode = consumer.countryCode || countryCode
    consumer.languageCode = consumer.languageCode || languageCode

    commit(types.SET_CONSUMER, consumer)
    dispatch('setLocale', locale)
  },

  /**
   * set application locale based on URL query param and browser settings
   * locale priority: URL query param -> browser settings (only if no URL query param provided) -> default fallback
   *
   * @param {Vuex} store
   * @param {String} urlLocale
   */
  setInitialLocale({ dispatch }, urlLocale) {
    // Prefer locale from URL query params
    let userLocale = determineLocale(urlLocale)

    // if user's locale is successfully determined, commit the data to the store
    if (userLocale) {
      return dispatch('setLocale', userLocale)
    }

    // Otherwise, try browser locale
    userLocale = determineBrowserLocale()

    dispatch('setLocale', userLocale)
  },

  /**
   * set application locale
   *
   * @param {Vuex} store
   * @param {String} locale
   */
  // eslint-disable-next-line complexity
  setLocale({ rootState, commit, getters }, locale) {
    if (locale) {
      commit(types.SET_LOCALE, locale)
      commit(types.SET_LOCALE_VALIDATED, false)
    }

    localizeApp(getters.locale)

    if (!rootState.isLocaleSelectorActive && !isTestOverrideEnabled(TEST_OVERRIDES.ONE_TRUST)) {
      loadOneTrust({
        locale: getters.locale,
        dataDomainScript: process.env.ONETRUST_DATA_DOMAIN_SCRIPT,
        scriptUrl: process.env.ONETRUST_LAUNCH_URL
      })
    }
  },

  /**
   * store previous locale for comparison purposes
   *
   * @param {Vuex} store
   * @param {String} previousLocale
   */
  setPreviousLocale({ commit }, previousLocale) {
    commit(types.SET_PREVIOUS_LOCALE, previousLocale)
  },

  /**
   * pushes experimentId to the history stack which is tracked by analytics
   *
   * @param {Vuex} store
   * @param {String} experimentId
   */
  trackExperiment({ commit }, experimentId) {
    if (experimentId) {
      commit(types.TRACK_EXPERIMENT, experimentId)
    }
  }
}

const mutations = {
  /**
   * Sets the non EMVCo spec consumer object.
   *
   * @param {Object} state
   * @param {Object} consumer
   */
  [types.SET_CONSUMER](state, consumer) {
    if (!consumer) return
    state.consumer = consumer
  },

  /**
   * Sets the list of app instances containing the user's browser and device info
   * @param {Object} state
   * @param {Array} appInstances
   */
  [types.SET_APP_INSTANCES](state, appInstances) {
    appInstances = appInstances || []
    // clears out the array and triggers Vue reactivity without reassigning the reference
    state.appInstances.splice(0)

    appInstances.forEach((appInstance) => {
      state.appInstances.push(formatDateData(appInstance))
    })
  },

  /**
   * Merges the list of app instances containing the user's browser and device info into the app instances list
   * @param {Object} state
   * @param {Array} appInstances
   */
  [types.ADD_APP_INSTANCES](state, appInstances) {
    appInstances.forEach((appInstance) => {
      const index = state.appInstances.findIndex((a) => a.id === appInstance.id)

      if (index !== -1) {
        Vue.set(state.appInstances, index, formatDateData(appInstance))
      } else {
        state.appInstances.push(formatDateData(appInstance))
      }
    })
  },

  /**
   * Set the flag on whether to fetch more appInstances
   * @param {Object} state
   * @param {Boolean} val
   */
  [types.SET_CALL_APP_INSTANCES](state, val) {
    state.callAppInstances = val
  },

  /**
   * Removes the app instance by id
   * @param {Object} state
   * @param {String} appInstanceId
   */
  [types.DELETE_APP_INSTANCE](state, appInstanceId) {
    const index = state.appInstances.findIndex(({ id }) => id === appInstanceId)

    if (index !== -1) {
      state.appInstances.splice(index, 1)
    }
  },

  /**
   * Set marketing opt-in
   * @param {Object} state
   * @param {Boolean} val
   */
  [types.SET_MARKETING_OPT_IN](state, val) {
    state.consumer.settings.marketingOptIn = val
  },

  /**
   * Update compliance accepted versions from latest versions
   * @param {Object} state
   * @param {Object} settings
   * @param {String} settings.privacyVersion
   * @param {String} settings.tncVersion
   * @param {String} settings.cookieVersion
   */
  [types.SET_COMPLIANCE_ACCEPTED_VERSION](state, settings) {
    state.consumer.settings = settings
  },

  /**
   * Set consumer mobile number
   * @param {Object} state
   * @param {Object} payload
   * @param {String} payload.countryCode
   * @param {String} payload.mobileNumber
   */
  [types.SET_MOBILE_NUMBER](state, payload) {
    const { countryCode, mobileNumber } = payload
    state.consumer.phone = { countryCode, mobileNumber }
  },
  /**
   * Set user locale
   * @param {Object} state
   * @param {String} locale - full locale ("en-US") or languageCode ("en")
   */
  [types.SET_LOCALE](state, locale) {
    const { languageCode, countryCode } = i18nData.getLocaleParts(locale)
    state.consumer.languageCode = languageCode
    state.consumer.countryCode = countryCode || state.consumer.countryCode
  },
  /**
   * @param {Object} state
   * @param {String} previousLocale
   */
  [types.SET_PREVIOUS_LOCALE](state, previousLocale) {
    state.previousLocale = previousLocale
  },

  /**
   * Set flag indicating if locale has been verified as supported
   * i.e. if country is live
   *
   * @param {Object} state
   * @param {boolean} isLocaleValidated
   */
  [types.SET_LOCALE_VALIDATED](state, val) {
    state.isLocaleValidated = val
  },

  /**
   * Push experimentId to the history stack which is tracked by analytics
   *
   * @param {Object} state
   * @param {String} experimentId
   */
  [types.TRACK_EXPERIMENT](state, experimentId) {
    state.experiments = [...state.experiments, experimentId]
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
